import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import ModernHeader from '@/components/header/ModernHeader';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/SEO/Breadcrumbs';
import CategoryHeader from '@/components/Categories/CategoryHeader';
import CategoryGrid from '@/components/Categories/CategoryGrid';
import MobileCategoryFilters from '@/components/Categories/MobileCategoryFilters';
import HorizontalCategoryFilter from '@/components/Categories/HorizontalCategoryFilter';
import LuxuryFilterSidebar from '@/components/Products/LuxuryFilterSidebar';
import { useProductsData } from '@/hooks/useProductsData';
import { useSEO } from '@/hooks/useSEO';
import { useIsMobile } from '@/hooks/use-mobile';
import { generateCategorySchema } from '@/utils/seoUtils';
import StructuredData from '@/components/SEO/StructuredData';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import BackButton from '@/components/navigation/BackButton';

const Categories = () => {
  const { categoryId } = useParams();
  const isMobile = useIsMobile();
  
  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(categoryId || 'all');
  const [sortBy, setSortBy] = useState('name');
  const [viewDensity, setViewDensity] = useState<'comfortable' | 'compact' | 'dense'>('comfortable');
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Data fetching
  const { products, isLoading, categories } = useProductsData(searchTerm, selectedCategory, sortBy);
  const { data: siteSettings } = useSiteSettings();

  // Update selected category when route changes
  useEffect(() => {
    if (categoryId) {
      setSelectedCategory(categoryId);
    }
  }, [categoryId]);

  // SEO optimization
  const currentCategory = categories?.find(cat => cat.id === selectedCategory);
  const pageTitle = currentCategory 
    ? `${currentCategory.name} - Premium Collection` 
    : 'Categories - Shop by Collection';
  
  useSEO({
    customTitle: pageTitle,
    customDescription: `Discover our curated ${currentCategory?.name || 'product'} collection. Premium quality items with luxury shopping experience.`,
    customKeywords: `${currentCategory?.name || 'categories'}, luxury shopping, premium products`,
    pageType: 'category',
    pageId: selectedCategory
  });

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Categories', href: '/categories' },
    ...(currentCategory ? [{ label: currentCategory.name, href: `/categories/${currentCategory.id}` }] : [])
  ];

  // Active filters count for mobile
  const activeFiltersCount = [
    searchTerm,
    selectedCategory !== 'all' ? selectedCategory : null,
    sortBy !== 'name' ? sortBy : null
  ].filter(Boolean).length;

  // Category structured data
  const categorySchema = currentCategory ? generateCategorySchema(currentCategory, products || [], siteSettings) : null;

  return (
    <div className="min-h-screen bg-background">
      <ModernHeader />
      <StructuredData data={categorySchema} />
      
      <main className="container mx-auto px-4 py-6">
        <BackButton to="/products" label="Back to Products" useSmartNavigation />
        {/* Breadcrumbs */}
        <div className="mb-6">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
        
        {/* Category Header */}
        <CategoryHeader
          title={currentCategory?.name || 'All Categories'}
          itemCount={products?.length || 0}
          sortBy={sortBy}
          setSortBy={setSortBy}
          viewDensity={viewDensity}
          setViewDensity={setViewDensity}
          isMobile={isMobile}
        />

        {/* Horizontal Category Filter */}
        <HorizontalCategoryFilter
          categories={categories}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          isLoading={isLoading}
        />

        <div className="flex gap-8 mt-8">
          {/* Desktop Sidebar */}
          {!isMobile && (
            <div className="w-80 flex-shrink-0">
              <LuxuryFilterSidebar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                sortBy={sortBy}
                setSortBy={setSortBy}
                categories={categories}
              />
            </div>
          )}

          {/* Main Content */}
          <div className="flex-1">
            <CategoryGrid
              products={products}
              isLoading={isLoading}
              viewDensity={viewDensity}
              isMobile={isMobile}
            />
          </div>
        </div>

        {/* Mobile Filter Button */}
        {isMobile && (
          <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
            <motion.button
              onClick={() => setShowMobileFilters(true)}
              className="bg-primary text-primary-foreground px-8 py-4 rounded-pill shadow-premium flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="font-medium">SORT / REFINE</span>
              {activeFiltersCount > 0 && (
                <span className="bg-accent text-accent-foreground px-2 py-1 rounded-full text-xs font-bold">
                  {activeFiltersCount}
                </span>
              )}
            </motion.button>
          </div>
        )}

        {/* Mobile Filters Modal */}
        <MobileCategoryFilters
          isOpen={showMobileFilters}
          onClose={() => setShowMobileFilters(false)}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          sortBy={sortBy}
          setSortBy={setSortBy}
          categories={categories}
        />
      </main>
      
      <Footer />
    </div>
  );
};

export default Categories;